/**
 * Phase 3 · Recovery Email Funnel — compact per-quote block.
 *
 * Reads GET /api/admin/quotes/{public_id}/funnel and renders one row
 * per stage (immediate / +4h / +16h / +28h) plus resume + conversion.
 * No charts, no expansions, intentionally minimal.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";

const STAGES = [
  { idx: 0, label: "Immediate" },
  { idx: 1, label: "+4h Offer" },
  { idx: 2, label: "+16h Reminder" },
  { idx: 3, label: "+28h Help" },
];

function Chip({ on, label }) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${on ? "bg-emerald-50 text-emerald-800 border border-emerald-200" : "bg-neutral-50 text-neutral-400 border border-neutral-200"}`}
      data-testid={`funnel-chip-${label.toLowerCase()}-${on ? "on" : "off"}`}
    >
      <span aria-hidden="true">{on ? "✓" : "—"}</span> {label}
    </span>
  );
}

function StageRow({ stage, row }) {
  const empty = !row || !row.sent;
  return (
    <div
      className="grid grid-cols-[140px_1fr] gap-3 py-1.5 items-center border-b border-neutral-100 last:border-b-0"
      data-testid={`funnel-stage-${stage.idx}`}
    >
      <div className="text-[11px] uppercase tracking-wider font-bold text-[var(--smoke)]">
        {stage.label}
      </div>
      {empty ? (
        <div className="text-[12px] text-neutral-400 italic">Pending</div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          <Chip on={row.sent} label="Sent" />
          <Chip on={row.delivered} label="Delivered" />
          <Chip on={row.opened} label="Opened" />
          <Chip on={row.clicked} label="Clicked" />
          {row.bounced && <Chip on={true} label="Bounced" />}
          {row.complained && <Chip on={true} label="Complained" />}
          {row.suppressed && <Chip on={true} label="Suppressed" />}
        </div>
      )}
    </div>
  );
}

export default function EmailFunnelPanel({ publicId }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!publicId) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/quotes/${publicId}/funnel`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "load_failed");
      }
    })();
    return () => { cancelled = true; };
  }, [publicId]);

  if (err) {
    return (
      <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5" data-testid="email-funnel-panel">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink)] mb-3">
          Email / Recovery Funnel
        </div>
        <div className="text-sm text-red-600" data-testid="email-funnel-error">
          Could not load funnel: {String(err)}
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5" data-testid="email-funnel-panel">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink)] mb-3">
          Email / Recovery Funnel
        </div>
        <div className="text-sm text-neutral-400 italic">Loading…</div>
      </div>
    );
  }

  const conv = data.conversion || { converted: false };
  const isCompletion = conv.converted && conv.conversion_type === "completion_price";

  return (
    <div className="border-2 border-[var(--ink)] rounded-2xl bg-white p-5" data-testid="email-funnel-panel">
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink)] mb-3">
        Email / Recovery Funnel
      </div>

      <div className="mb-3">
        {STAGES.map((s) => (
          <StageRow key={s.idx} stage={s} row={data.stages?.[s.idx]} />
        ))}
      </div>

      <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 border-t-2 border-neutral-200 pt-3 items-center">
        <div className="text-[11px] uppercase tracking-wider font-bold text-[var(--smoke)]">
          Quote Resume
        </div>
        <div className="text-sm" data-testid="funnel-resume-count">
          {data.resume_count > 0
            ? `Resumed ✓ · ${data.resume_count} time${data.resume_count === 1 ? "" : "s"}`
            : <span className="text-neutral-400 italic">Not resumed</span>}
        </div>
      </div>

      <div className="grid grid-cols-[140px_1fr] gap-3 py-1.5 items-center">
        <div className="text-[11px] uppercase tracking-wider font-bold text-[var(--smoke)]">
          Order
        </div>
        <div className="text-sm" data-testid="funnel-conversion">
          {!conv.converted ? (
            <span className="text-neutral-400 italic">Not yet converted</span>
          ) : (
            <span>
              Converted —{" "}
              <span className={isCompletion ? "text-emerald-700 font-bold" : "text-neutral-800 font-bold"}>
                {isCompletion ? "Completion Price" : "Standard Price"}
              </span>
              {isCompletion && conv.savings_amount != null && (
                <span className="text-[12px] text-neutral-500 ml-2">
                  · Saved ${Number(conv.savings_amount).toFixed(2)}
                  {conv.original_subtotal != null && (
                    <> · Orig ${Number(conv.original_subtotal).toFixed(2)}</>
                  )}
                  {conv.completion_price_subtotal != null && (
                    <> → ${Number(conv.completion_price_subtotal).toFixed(2)}</>
                  )}
                </span>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}
